import axios from "axios";
import { toast } from "react-toastify";
import SecureSS from "../utils/SecureStorage";
import { extractErrorMessage } from "../utils/errorUtils";
import { createTelemetryAxiosInterceptor } from "@hoteltradergit/ht-telemetry-ui-lib";
import { logger, buildApiContext } from "../config/telemetry";

const DOWNLOAD_FILE = "DOWNLOAD_FILE/";

const createAxiosInstance = (baseURL) => {
  const HEADERS = {
    "Content-Type": "application/json",
    // Helps backend distinguish API calls from browser navigation
    "X-Requested-With": "XMLHttpRequest",
  };

  const FILE_HEADERS = {
    "Content-Type": "multipart/form-data",
    "X-Requested-With": "XMLHttpRequest",
  };

  const axiosInstance = axios.create({
    headers: HEADERS,
    baseURL,
  });

  // ---------- TELEMETRY INTERCEPTOR (Rapid7 request/response/error logging) ----------
  // Registered first so its request handler runs last (after auth rewrites the
  // URL) and its error handler sees the original AxiosError before the session
  // handler below rewrites it to SESSION_EXPIRED.
  const telemetry = createTelemetryAxiosInterceptor(logger, {
    baseUrl: baseURL,
    buildContext: buildApiContext,
  });
  axiosInstance.interceptors.request.use(telemetry.onRequest);
  axiosInstance.interceptors.response.use(
    telemetry.onResponse,
    telemetry.onError
  );

  const handleInvalidSession = ({ statusText, noRedirectToPreviousPage }) => {
    toast.error("Invalid session, Please login to continue");

    // Clear all authentication data
    SecureSS.clear();

    // Notify React app (AuthProvider) to redirect
    window.dispatchEvent(
      new CustomEvent("auth:session-expired", {
        detail: { noRedirectToPreviousPage },
      })
    );
  };

  // ---------- REQUEST INTERCEPTOR ----------
  axiosInstance.interceptors.request.use(
    (config) => {
      const token = SecureSS.getToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      if (config.url.startsWith(DOWNLOAD_FILE)) {
        config.url = config.url.replace(DOWNLOAD_FILE, "");
        config.responseType = "blob";
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  // ---------- RESPONSE INTERCEPTOR (central auth handling) ----------
  axiosInstance.interceptors.response.use(
    (response) => response,
    (error) => {
      const { response } = error;

      // Only 401 means the session is invalid. 403 is NOT treated as a session
      // failure: the backend uses 403 as a business response (e.g. insufficient
      // balance / forbidden action), so it must propagate to the caller instead
      // of clearing storage and logging the user out.
      if (response && response.status === 401) {
        handleInvalidSession({ statusText: response.statusText });

        // Reject with a special error so caller can decide what to do
        // but we don't show extra popups or try to re-auth here.
        return Promise.reject(new Error("SESSION_EXPIRED"));
      }

      return Promise.reject(error);
    }
  );

  const handleError = (error) => {
    // Auth already handled globally – nothing else to show
    if (error?.message === "SESSION_EXPIRED") {
      return;
    }

    const { response, message } = error;
    if (response) {
      const { status, statusText, data } = response;
      switch (status) {
        // 401 is handled in the interceptor (session invalidation).
        // 403 is a business/forbidden response — surface its message to the
        // caller like other 4xx errors; do NOT log the user out.
        case 400:
        case 403:
        case 417:
          if (data) {
            // Extract the error message using shared utility
            return extractErrorMessage(data);
          }
          break;

        case 500:
          toast.error(
            <div>
              <div>
                <b>Message:</b> {statusText}
              </div>
              <div>
                <b>Status code:</b> {status}
              </div>
              <div>{data.message || data.error || JSON.stringify(data)}</div>
            </div>
          );
          return statusText;

        default:
          toast.error(data?.message || statusText);
      }
    }
    return message;
  };

  const handleRequest = async (
    method,
    path,
    data,
    isFileUpload = false,
    config = undefined
  ) => {
    try {
      axiosInstance.defaults.headers = isFileUpload ? FILE_HEADERS : HEADERS;

      const response = await axiosInstance[method](path, data, config);
      return { ...response, body: response.data };
    } catch (error) {
      const processed = handleError(error);

      // If SESSION_EXPIRED, processed will be undefined – just rethrow
      if (!processed) {
        throw error;
      }

      // Wrap non-error values into an Error so callers can handle consistently.
      // Ensure we surface the backend message string instead of a JSON blob.
      const normalizedMessage =
        typeof processed === "string"
          ? processed
          : processed?.message ||
            processed?.error ||
            JSON.stringify(processed);

      throw new Error(normalizedMessage);
    }
  };

  const handleRequestForFile = async (method, path, data) => {
    return handleRequest(method, DOWNLOAD_FILE + path, data);
  };

  const handleRequestForFileUpload = async (method, path, data, config) => {
    return handleRequest(method, path, data, true, config);
  };

  return {
    getRequest: (path) => handleRequest("get", path),
    postRequest: (path, data, config) =>
      handleRequest("post", path, data, false, config),
    putRequest: (path, data) => handleRequest("put", path, data),
    delRequest: (path) => handleRequest("delete", path),
    postFileRequest: (path, data) => handleRequestForFile("post", path, data),
    postFileUploadRequest: (path, data, config) =>
      handleRequestForFileUpload("post", path, data, config),
    getFileRequest: (path) => handleRequestForFile("get", path),
  };
};

export default createAxiosInstance;
